import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn
} from '@angular/forms';
import { KalDate } from '@kalidea/kaligraphi';
import isInteger from 'lodash-es/isInteger';
import { arrayEmpty, arrayNotEmpty, arrayOfMoreThanOne } from 'src/app/utils/array.utils';
import { toNumber } from 'src/app/utils/number.utils';
import { isNullOrEmpty, removeSpaces, trim, removePhoneCarSeparator } from 'src/app/utils/string.utils';
import { environment } from 'src/environments/environment';
import { LigneFraisAnnulationModel } from 'src/graphql/generated-types';

export class PasswordComplianceErrors implements ValidationErrors {
    passwordMinLength?: boolean;

    passwordNeedUppercaseLetter?: boolean;

    passwordNeedLowercaseLetter?: boolean;

    passwordNeedNumber?: boolean;

    passwordNoSpace?: boolean;

    passwordNoSpecialCharacter?: boolean;
}

// eslint-disable-next-line max-len
export const EMAIL_REGEXP = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**
 * new version of Validator to avoid dependency
 */
export class FormValidator {

    /**
     * validation of password in boule
     */
    static passwordValidator(control: AbstractControl): ValidationErrors | null {
        const controlValue = ('' + control.value);

        const validationErrors: PasswordComplianceErrors = {
            passwordMinLength: false,
            passwordNeedUppercaseLetter: false,
            passwordNeedLowercaseLetter: false,
            passwordNeedNumber: false,
            passwordNoSpace: false,
            passwordNoSpecialCharacter: false
        };

        if (controlValue.trim().length < 14) {
            validationErrors.passwordMinLength = true;
        }

        if (controlValue.toLocaleLowerCase() === controlValue) {
            validationErrors.passwordNeedUppercaseLetter = true;
        }

        if (controlValue.toLocaleUpperCase() === controlValue) {
            validationErrors.passwordNeedLowercaseLetter = true;
        }

        if (!/\d/.test(controlValue)) {
            validationErrors.passwordNeedNumber = true;
        }

        if (/\s/.test(controlValue.trim())) {
            validationErrors.passwordNoSpace = true;
        }

        if (!/[@#$£¤§%^&*()_+–={}\[\]\|:;,.?!\/<>¨µ~`²]/.test(controlValue)) {
            validationErrors.passwordNoSpecialCharacter = true;
        }

        return Object.keys(validationErrors).some(e => validationErrors[e] === true) ? {invalidformatpassword: true} : null;
    }

    /**
     * Check if end age is over start age
     */
    static overStartAge(control: AbstractControl): ValidationErrors | null {
        const startAge = +control.get('age_start').value;
        const endAge = +control.get('age_end').value;

        if (startAge > endAge) {
            return {invalidStartAge: true};
        } else {
            return null;
        }
    }

    /**
     * Check if end number is over start number
     */
    static overNumber(start: string, end: string, customErrorSuffix: string = ''): ValidationErrors | null {
        return (control: AbstractControl) => {
            const inputStart = +control.get(start).value;
            const inputEnd = +control.get(end).value;

            if (inputStart && inputEnd && inputStart > inputEnd) {
                return {['invalidstartnumber' + customErrorSuffix]: true};
            } else {
                return null;
            }
        };
    }

    /**
     * Check if worktime is in number range
     */
    static percent(percent: UntypedFormGroup): ValidationErrors | null {
        const value = percent.value;
        if (!value || value >= 0 && value <= 100) {
            return null;
        } else {
            return {invalidpercent: true};
        }
    }

    /**
     * Check if the field is a valid amount
     */
    static nonNullAmount(control: AbstractControl): ValidationErrors | null {
        const numberError = FormValidator.number(control);
        const decimalError = FormValidator.decimal184(control);
        const isNullOrNegative = +(Math.round(control.value * 100) / 100) <= 0;
        if (!!numberError || !!decimalError || isNullOrNegative) {
            return {nullamount: true};
        } else {
            return null;
        }
    }

    /**
     * Check if the field value is a number format
     */
    static number(control: AbstractControl): ValidationErrors | null {
        const value = removeSpaces(control.value);
        if (value && isNaN(toNumber(value))) {
            return {invalidNumber: true};
        } else {
            return null;
        }
    }

    /**
     * Check if the field value is a number with a maximum of 2 fraction digits
     */
    static numberTwoDecimal(control: AbstractControl): ValidationErrors | null {
        const regExp = /^\-?[0-9]*(\.[0-9]{1,2})?$/;
        const value = removeSpaces(control.value);
        const number = toNumber(value);

        if (!isNaN(number) && !regExp.test('' + number)) {
            return {invalidDecimal: true};
        }
        return null;
    }

    /**
     * Check the validity of an integer value
     */
    static int(control: AbstractControl): ValidationErrors | null {
        const value = removeSpaces(control.value);

        if (!value && value !== '0') {
            return null;
        }

        const number = toNumber(value);
        if (isNaN(number)
            || !isInteger(number)
            || number < environment.numbers.int32MinValue
            || number > environment.numbers.int32MaxValue) {
            return {invalidint: true};
        } else {
            return null;
        }
    }

    /**
     * Check the validity of a decimal value for SQL decimal(18,4)
     */
    static decimal184(control: AbstractControl): ValidationErrors | null {
        const value = removeSpaces(control.value);
        const number = toNumber(value);

        if (isNaN(number)
            || number < environment.numbers.decimal184MinValue
            || number > environment.numbers.decimal184MaxValue) {
            return {invalidDecimal: true};
        } else {
            return null;
        }
    }

    /**
     * Check the validity of a decimal value for SQL decimal(18,2)
     */
    static decimal182(control: AbstractControl): ValidationErrors | null {
        const value = removeSpaces(control.value);
        const number = toNumber(value);

        if (isNaN(number)
            || number < environment.numbers.decimal182MinValue
            || number > environment.numbers.decimal182MaxValue) {
            return {invalidDecimal: true};
        } else {
            return null;
        }
    }

    /**
     * Check if value is not null or not empty
     */
    static isNotNull(control: AbstractControl): ValidationErrors | null {
        const isKalDate = control.value instanceof KalDate;
        if (!isKalDate && isNullOrEmpty(control.value)) {
            return {invalidValue: true};
        } else {
            return null;
        }
    }

    /**
     * Check if the phone number has the right format.
     * @see https://regex101.com/r/ldL2es/3/
     */
    static phoneNumber(control: AbstractControl): ValidationErrors | null {
        const value = removePhoneCarSeparator(control.value);
        const regexPhone = /^(\+|00|0)?\d(\d ?){1,10}$/;
        return value && !regexPhone.test(value) ? {invalidPhoneFormat: true} : null;
    }

    /**
     * Check the password format
     */
    static password(password: UntypedFormGroup): ValidationErrors | null {
        return password.value && (!(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9]).{7,}$/).test(password.value)) ? {invalidPassword: true} : null;
    }

    /**
     * Field validator: password match
     */
    static passwordRepeat(pwdControlName: string, repeatPwdControlName: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const passwordValue = control.get(pwdControlName).value;
            const repeatPasswordValue = control.get(repeatPwdControlName).value;
            return (passwordValue && repeatPasswordValue) && repeatPasswordValue !== passwordValue ? {invalidmatchpassword: true} : null;
        };
    }

    /**
     * Check if the control value is a valid SIRET
     * @see https://fr.wikipedia.org/wiki/Formule_de_Luhn
     */
    static siret(control: AbstractControl): ValidationErrors | null {
        const siretNumber = removeSpaces(control.value);

        if (!siretNumber) {
            return null;
        }

        if (siretNumber.length !== 14) {
            return {invalidSiret: true};
        } else {

            let bal = 0;
            let total = 0;

            for (let i = 13; i >= 0; i--) {
                const step = (siretNumber.charCodeAt(i) - 48) * (bal + 1);
                total += (step > 9) ? step - 9 : step;
                bal = 1 - bal;
            }

            return (total % 10 === 0) ? null : {invalidSiret: true};
        }

    }

    static greaterThanDateValidator(greaterThanDate: KalDate): (control: AbstractControl) => ValidationErrors | null {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            // On clone la valeur pour éviter un bug lors de la désactivation d'un formGroup comprenant un control avec ce validateur
            const value = new KalDate(control.value);
            return value.isAfter(greaterThanDate) ? null : {greaterthandate: {requiredDate: greaterThanDate.toString()}};
        };
    }

    static minDateValidator(minDate: KalDate): (control: AbstractControl) => ValidationErrors | null {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            // On clone la valeur pour éviter un bug lors de la désactivation d'un formGroup comprenant un control avec ce validateur
            const value = new KalDate(control.value);
            return value.isSameOrAfter(minDate) ? null : {mindate: {requiredDate: minDate.toString()}};
        };
    }

    static maxDateValidator(maxDate: KalDate = new KalDate()): (control: AbstractControl) => ValidationErrors | null {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            // On clone la valeur pour éviter un bug lors de la désactivation d'un formGroup comprenant un control avec ce validateur
            const value = new KalDate(control.value);
            return value.isSameOrBefore(maxDate) ? null : {maxdate: {requiredDate: maxDate.toString()}};
        };
    }

    static date(control: AbstractControl): ValidationErrors | null {
        if (!control.value || (control.value as KalDate).valid) {
            return null;
        }

        return {invaliddate: true};
    }

    static dateRangeValidator(startDateControlName: string, endDateControlName: string, customErrorKey?: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            if (control) {
                const startDate = control.get(startDateControlName).value as KalDate;
                const endDate = control.get(endDateControlName).value as KalDate;
                return FormValidator.checkDateRange({startDate, endDate, customErrorKey});
            }

            return null;
        };
    }

    static dateTimeRangeValidator(startDateControlName: string, endDateControlName: string, startTimeControlName: string, endTimeControlName: string, customErrorKey?: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const parseDateTime: (dateAsString: string, time: string) => KalDate = (dateAsString, time) => new KalDate(dateAsString).set('hours', +time.substring(0, 2)).set('minutes', +time.substring(3, 5));
            if (control) {
                const startTime = control.get(startTimeControlName).value;
                const endTime = control.get(endTimeControlName).value;
                const startDate = parseDateTime(control.get(startDateControlName).value, startTime);
                const endDate = parseDateTime(control.get(endDateControlName).value, endTime);
                return FormValidator.checkDateRange({startDate, endDate, customErrorKey});
            }

            return null;
        };
    }

    static checkDateRange({
                              startDate,
                              endDate,
                              customErrorKey
                          }: { startDate: KalDate, endDate: KalDate, customErrorKey: string }): { [p: string]: boolean } {
        if (startDate !== null && endDate !== null &&
            (startDate?.isAfter?.(endDate) || endDate?.isBefore?.(startDate))) {
            return {[customErrorKey ? customErrorKey : 'invaliddaterange']: true};
        }
        return null;
    }

    // Validator for control on AppDateRange when both startDate and endDate are required
    static requireBothDatesValidator(control: AbstractControl): ValidationErrors | null {
        return ['startDate', 'endDate'].some(e => !control.value?.[e]) ? {requirebothdates: true} : null;
    }

    /**
     * Check if mail is valid
     */
    static email(control: AbstractControl): ValidationErrors | null {
        const value = trim(control.value);
        // eslint-disable-next-line max-len
        if (value && !EMAIL_REGEXP.test(value)) {
            return {invalidEmail: true};
        } else {
            return null;
        }
    }

    static valueAllTrue(control: AbstractControl): ValidationErrors | null {
        const hasOneTruthyValue = Object.keys(control.value).some(b => control.value[b] === true);
        return hasOneTruthyValue ? null : {notruthyValue: true};
    }

    /**
     * Whether the given form array have at least one choice with a valid value
     */
    static hasChoice(formArray: UntypedFormArray): ValidationErrors | null {
        return formArray.controls.every(c => !c.value || !('' + c.value).trim()) ? {invalidArray: true} : null;
    }

    static commissionHasEmail(control: AbstractControl): ValidationErrors | null {
        return !control.value
            ? {noemailcommission: true}
            : null;
    }

    static hasExistingValue(...values: string[]): ValidationErrors | null {
        return (control: AbstractControl) => {
            const selectedValue = values.find(value => value === control.value);
            return selectedValue ? {existingvalue: {value: selectedValue}} : null;
        };
    }

    static greaterThan(value: number): (control: AbstractControl) => ValidationErrors | null {
        return (control: AbstractControl): ValidationErrors | null => {
            const number = toNumber(removeSpaces(control.value));
            return (!isNaN(number) && number <= value) ? {notgreaterthan: {value}} : null;
        };
    }

    static numberRangeValidator(min: number, max: number): (control: AbstractControl) => ValidationErrors | null {
        return (control: AbstractControl): ValidationErrors | null => {
            const number = toNumber(removeSpaces(control.value));
            return (!isNaN(number) && (number < min || number > max)) ? {invalidNumberRange: {min, max}} : null;
        };
    }

    static requiredLength(requiredLength: number): ValidatorFn {
        return (control: UntypedFormControl) => {
            return control.value?.length !== requiredLength ? {requiredLength: false} : null;
        };
    }

    static isOneOf(...values: any[]): (control: AbstractControl) => ValidationErrors | null {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            return !values.includes(value) ? {isnotoneof: {value, allowed: values}} : null;
        };
    }

    static isOneOfComponentDynamicListById<T>(component: T, keyList: string, keyId: string): (control: AbstractControl) => ValidationErrors | null {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            return arrayEmpty(component?.[keyList]) || !component[keyList].find(e => e[keyId] === value) ? {isnotoneofcomponentdynamiclistbyid: true} : null;
        };
    }

    static formArrayNotEmpty(control: AbstractControl): ValidationErrors | null {
        return arrayEmpty(control.value) ? {formarrayisempty: true} : null;
    }

    static formArrayNoOverlappingRange(start: string, end: string): ValidatorFn {
        return (control): ValidationErrors | null => {
            const lines: LigneFraisAnnulationModel[] = control.value;
            if (!arrayOfMoreThanOne(lines)) {
                return null;
            }
            const overlaps = [];

            for (let i = 0; i < lines.length - 1; i++) {
                const line1 = lines[i];

                for (let j = i + 1; j < lines.length; j++) {
                    const line2 = lines[j];

                    // Check pour les nulls ?
                    if ((line1[end] <= line2[end] && line1[end] > line2[start])
                        || (line1[start] < line2[end] && line1[start] >= line2[start])) {

                        overlaps.push([i, j]);
                    }
                }
            }

            return arrayNotEmpty(overlaps) ? {overlappingranges: overlaps} : null;
        };
    }

    static isUrlAbs(control: AbstractControl): ValidationErrors | null {
        const UrlRegexp = [
            '([a-z0-9+!*(),;?&=\\$_.-]+(\\:[a-z0-9+!*(),;?&=\\$_.-]+)?@)?',     // USER AND PASS (optional)
            '[a-z0-9+\\$_-]+(?:\\.[a-z0-9+\\$_-]+)*',                           // HOSTNAME ou IP (ex. http://localhost, http://routerlogin)
            '(?:\\:[0-9]{2,5})?',                                               // PORT (optional)
            '(?:\\/(?:[a-z0-9+\\$_~-]\\.?)+)*\\/?',                             // PATH  (optional)
            '(?:\\?[a-z+&\\$_.-][a-z0-9;:@/&%=+\\$_.-|]*)?',                    // GET Query (optional)
            '(?:\\#[a-z_.-][a-z0-9+\\$_.-]*)?$'
        ];                              // ANCHOR (optional)
        const value = trim(control.value);
        return (!!value && !(new RegExp(UrlRegexp.join(''), 'gi')).test(value))
            ? {invalidurl: true}
            : null;
    }
}

